import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {ChipsOption, Option} from 'platform/components';
import {useLocale} from 'platform/locale';

import {isNil, isNotNil, last, length, omit} from 'ramda';

import {usePriceReport} from '../../../hooks/usePriceReport';
import {useDrillDownQuery} from '../../../store/omneticApi';
import {DrillDownLevelName} from '../../../types/api/drillDown';
import {featureFlags} from '../../../utils/featureFlags';
import {getPermanentLevelFilters} from '../../DrillDown/components/DrillDown/utils/getPermanentLevelFilters';

const useGetCounts = (
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, string>>
) => {
  const {requestData} = usePriceReport();
  const {language} = useLocale();
  const levelFilters = getPermanentLevelFilters(omit([optionKey], filters));
  const {data: drillDownData, isLoading} = useDrillDownQuery(
    isNotNil(requestData?.market)
      ? {
          levels: [
            ...levelFilters,
            {
              level: optionKey,
              values: [],
              priority: length(levelFilters) + 1,
            },
          ],
          currency: 'CZK', // We don't know currency, but we are not going to use prices
          regions: [requestData.market],
          lang: language,
        }
      : skipToken,
    {
      skip:
        isNil(filters.make) ||
        isNil(filters.model) ||
        !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS),
    }
  );

  const counts = drillDownData?.statisticsAggregated.map((item) => ({
    option: last(item.levels)?.value,
    count: item.statistics.reduce((acc, curr) => acc + curr.amount, 0),
  }));
  return {counts, isLoading};
};

export interface FilterRelevantOptionsResult {
  options: ChipsOption[];
  isLoading: boolean;
}

export const useFilterRelevantOptions = (
  options: Option[],
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, string>>
): FilterRelevantOptionsResult => {
  const {counts, isLoading} = useGetCounts(optionKey, filters);
  const getCount = (option: Option) =>
    counts?.find((count) => count.option === option.value)?.count;

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return {options, isLoading: false};
  }

  const mappedOptions = options
    .filter((item) => {
      const count = getCount(item);
      return isNotNil(count) && count > 0;
    })
    .map((item) => ({
      ...item,
      suffix: `(${getCount(item)})`,
      isDisabled: false,
    }));

  return {options: mappedOptions, isLoading};
};

export interface RelevantPowerOptionsResult {
  options: ChipsOption[];
  isLoading: boolean;
}

export const useGetRelevantPowerOptions = (
  filters: Partial<Record<DrillDownLevelName, string>>
): RelevantPowerOptionsResult => {
  const {counts, isLoading} = useGetCounts('power', filters);

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return {options: [], isLoading: false};
  }

  const options =
    counts
      ?.sort((a, b) => {
        const aValue = Number(a.option);
        const bValue = Number(b.option);
        return aValue - bValue;
      })
      .map((count) => ({
        value: count.option ?? '?',
        label: `${count.option} kW`,
        suffix: `(${count.count})`,
      })) ?? [];
  return {options, isLoading};
};
